import { Capacitor } from '@capacitor/core';

const DEVICE_ID_KEY = 'sp_device_id';

/**
 * Benzersiz ve kalıcı bir cihaz kimliği (UUID) döndürür veya oluşturur.
 */
export const getOrCreateDeviceId = () => {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        id = crypto.randomUUID();
      } else {
        id = 'dev_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
      }
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch (e) {
    console.warn('localStorage erişilemedi, geçici deviceId kullanılıyor:', e);
    return 'dev_temp_' + Date.now();
  }
};

/**
 * İstemcinin platform tipini tespit eder:
 * - 'android_native'
 * - 'ios_native'
 * - 'pwa' (Standalone Web Uygulaması)
 * - 'web' (Masaüstü veya mobil tarayıcı)
 */
export const getPlatformType = () => {
  if (Capacitor.isNativePlatform()) {
    return Capacitor.getPlatform() === 'android' ? 'android_native' : 'ios_native';
  }

  const isStandalone = (
    (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    (typeof window !== 'undefined' && window.navigator && window.navigator.standalone) ||
    (typeof document !== 'undefined' && document.referrer && document.referrer.includes('android-app://'))
  );

  if (isStandalone) {
    return 'pwa';
  }

  return 'web';
};

/**
 * Cihaz ve tarayıcı detaylarını kullanıcı dostu ve ayrıntılı şekilde döndürür.
 */
export const getDeviceInfo = () => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const platform = getPlatformType();
  const deviceId = getOrCreateDeviceId();

  let deviceName = 'Bilinmeyen Cihaz';
  let osName = 'Bilinmeyen İşletim Sistemi';

  if (platform === 'android_native') {
    deviceName = 'Android Cihaz';
    osName = 'Android (Mobil Uygulama)';
  } else if (platform === 'ios_native') {
    deviceName = 'Apple Cihaz';
    osName = 'iOS (Mobil Uygulama)';
  } else {
    if (/Windows/i.test(ua)) {
      deviceName = 'Windows Bilgisayar';
      osName = 'Windows';
    } else if (/Macintosh|Mac OS/i.test(ua)) {
      deviceName = 'Mac Bilgisayar';
      osName = 'macOS';
    } else if (/iPhone/i.test(ua)) {
      deviceName = 'iPhone';
      osName = 'iOS';
    } else if (/iPad/i.test(ua)) {
      deviceName = 'iPad';
      osName = 'iPadOS';
    } else if (/Android/i.test(ua)) {
      deviceName = 'Android Telefon / Tablet';
      osName = 'Android';
    } else if (/Linux/i.test(ua)) {
      deviceName = 'Linux Bilgisayar';
      osName = 'Linux';
    }
  }

  let browserName = 'Tarayıcı';
  if (platform === 'android_native' || platform === 'ios_native') {
    browserName = 'Sectorpilot Native App';
  } else if (platform === 'pwa') {
    browserName = 'Sectorpilot PWA (Yüklü Web Uygulaması)';
  } else {
    if (/Edg/i.test(ua)) browserName = 'Microsoft Edge';
    else if (/Chrome/i.test(ua)) browserName = 'Google Chrome';
    else if (/Firefox/i.test(ua)) browserName = 'Mozilla Firefox';
    else if (/Safari/i.test(ua)) browserName = 'Apple Safari';
    else if (/Opera|OPR/i.test(ua)) browserName = 'Opera';
  }

  return {
    deviceId,
    deviceName,
    osName,
    browserName,
    platform,
    userAgent: ua.substring(0, 150),
    screenResolution: typeof window !== 'undefined' && window.screen ? window.screen.width + 'x' + window.screen.height : '',
    language: typeof navigator !== 'undefined' ? (navigator.language || navigator.userLanguage) : 'tr-TR',
    appVersion: '1.0.0'
  };
};

/**
 * Platform bilgilerini geriye dönük uyumlu formatta döndürür.
 */
export const getPlatformInfo = () => {
  return getDeviceInfo();
};

/**
 * Uygulama arka plan / ön plan geçişlerini dinler (PWA, Android Native, Web Resume)
 */
export const initAppLifecycleListener = (onForegroundResume) => {
  let backgroundTimestamp = 0;

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      backgroundTimestamp = Date.now();
    } else if (document.visibilityState === 'visible') {
      const backgroundDuration = backgroundTimestamp > 0 ? (Date.now() - backgroundTimestamp) : 0;
      if (typeof onForegroundResume === 'function') {
        onForegroundResume({ backgroundDuration });
      }
    }
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  return () => {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
  };
};
